import React, { useState } from 'react';
import { FaWhatsapp, FaTelegram, FaFacebookMessenger, FaPhone } from 'react-icons/fa';
import { Loader } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import GoogleMapLocationPicker from '../components/GoogleMapLocationPicker';
import { getTranslatedCities } from '../lib/constants';
import { RichTextEditor } from './RichTextEditor';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';

interface SellerProfileFormProps {
  initialData: any;
  onSubmit: (formData: any) => Promise<void>;
  onCancel: () => void;
  selectedLocation: string;
  setSelectedLocation: (location: string) => void;
  pickupLocations: string[];
  setPickupLocations: (locations: string[]) => void;
}

export const SellerProfileForm: React.FC<SellerProfileFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  selectedLocation,
  setSelectedLocation,
  pickupLocations,
  setPickupLocations,
}) => {
  const { t, language } = useLanguage();
  // Initialize from props on mount — component remounts each time editing starts,
  // so useState(initialData) is sufficient. No useEffect sync needed.
  const [formData, setFormData] = useState(initialData);
  const [showPickupPicker, setShowPickupPicker] = useState(false);
  const [newPickup, setNewPickup] = useState<string>('');
  const [newPickupObj, setNewPickupObj] = useState<{ lat: number; lng: number; address: string } | null>(null);
  const [loading, setLoading] = useState(false);

  const cities = getTranslatedCities(language);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePickupLocationSelect = (location: any) => {
    // Create a location object with all required fields
    const locationObject = {
      lat: location.lat,
      lng: location.lng,
      address: location.fullAddress || location.address,
      displayName: location.displayName || location.address,
      fullAddress: location.fullAddress || location.address
    };
    
    // Set both the object and the display name
    setNewPickupObj(locationObject);
    setNewPickup(locationObject.displayName); // Use displayName from the map component
  };

  const handleAddPickup = async () => {
    if (!newPickup || !newPickupObj) return;
    if (pickupLocations.length >= 10) {
      toast.error('Maximum of 10 pickup locations allowed.');
      return;
    }
    
    // Add the full location object to the array
    setPickupLocations([...pickupLocations, {
      ...newPickupObj,
      displayName: newPickup // Use the potentially edited display name
    }]);
    
    setNewPickup('');
    setNewPickupObj(null);
    setShowPickupPicker(false);
  };

  const handleDeletePickup = async (index: number) => {
    const updatedPickups = pickupLocations.filter((_, i) => i !== index);
    setPickupLocations(updatedPickups);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Call the onSubmit function passed from the parent component
      await onSubmit({
        ...formData,
        location: selectedLocation,
        pickup_locations: pickupLocations, // Now contains full location objects
      });
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative h-full pb-40">
      {/* Top Bar */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">{t('storeProfile')}</h2>
        <button
          onClick={onCancel}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label={t('close')}
        >
          <X className="w-6 h-6 text-gray-500" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8 overflow-y-auto h-full p-4">
        {/* Basic Info Card */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 space-y-6">
          {/* Store Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('storeName')}
            </label>
            <input
              type="text"
              name="store_name"
              value={formData.store_name || ''}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              required
            />
          </div>

          {/* Location (City) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('location')}
            </label>
            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            >
              <option value="">{t('selectCity')}</option>
              {cities.map((city) => (
                <option key={city.id} value={city.id}>
                  {city.name}
                </option>
              ))}
            </select>
          </div>

          {/* Store Description (Using Modular RichTextEditor) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('storeDescription')}
            </label>
            <div className="border border-gray-200 rounded-lg">
              <RichTextEditor
                initialContent={formData.store_description || ''}
                onChange={(html) =>
                  setFormData((prev) => ({ ...prev, store_description: html }))
                }
              />
            </div>
          </div>
        </div>

        {/* Contact Info Card */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 space-y-6">
          <h3 className="text-lg font-semibold text-gray-900">{t('contactInfo')}</h3>
          <div className="grid gap-6 md:grid-cols-2">
            {[
              { name: 'whatsapp', label: t('whatsappNumber'), icon: <FaWhatsapp className="w-5 h-5" /> },
              { name: 'telegram', label: t('telegramUsername'), icon: <FaTelegram className="w-5 h-5" /> },
              { name: 'messenger', label: t('messengerUsername'), icon: <FaFacebookMessenger className="w-5 h-5" /> },
              { name: 'phone', label: t('phoneNumber'), icon: <FaPhone className="w-5 h-5" /> },
            ].map((field) => (
              <div key={field.name} className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  {field.icon}
                  {field.label}
                </label>
                <input
                  type="text"
                  name={field.name}
                  value={formData[field.name] || ''}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Pickup Locations Card */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">{t('pickupLocations')}</h3>
          {pickupLocations.map((loc, i) => (
            <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
              <span className="text-gray-800">{loc.displayName}</span>
              <button
                type="button"
                onClick={() => handleDeletePickup(i)}
                className="text-red-600 hover:text-red-800 text-sm"
              >
                {t('delete')}
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setShowPickupPicker(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors"
          >
            {t('addPickupLocation')}
          </button>

          {showPickupPicker && (
            <div className="mt-4 border rounded-xl p-4">
              <GoogleMapLocationPicker 
                onLocationSelect={handlePickupLocationSelect} 
                allowManualEdit={false} // Disable the built-in display name editor
              />
              {newPickup && (
                <div className="mt-2">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-700">{newPickup}</span>
                  </div>
                  {/* Manual edit option with improved UI */}
                  <div className="mb-2 border-l-4 border-primary-500 pl-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                      <span className="mr-2">{t('editDisplayName')}</span>
                      <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-0.5 rounded-full">
                        {t('customLabel')}
                      </span>
                    </label>
                    <input
                      type="text"
                      value={newPickup}
                      onChange={(e) => setNewPickup(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                      placeholder={t('businessNameCity')}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {t('displayNameHelp')}
                    </p>
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={handleAddPickup}
                      className="px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors"
                    >
                      {t('addLocation')}
                    </button>
                  </div>
                </div>
              )}
              <button
                type="button"
                onClick={() => {
                  setShowPickupPicker(false);
                  setNewPickup('');
                  setNewPickupObj(null);
                }}
                className="mt-2 text-sm text-gray-500 hover:underline"
              >
                {t('cancel')}
              </button>
            </div>
          )}
        </div>

        {/* Submit and Cancel Buttons */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-4 pt-8 border-t border-gray-100">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            {t('cancel')}
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-3 text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <Loader className="w-4 h-4 animate-spin" />
                {t('saving')}
              </span>
            ) : (
              t('saveChanges')
            )}
          </button>
        </div>
      </form>
    </div>
  );
};